import React, {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState
} from 'react'

import { Popover, SxProps, ThemeProvider } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'

import { getTheme } from 'themes'

import { BOARD } from './accents'

export interface FunctionOption {
  id: string
  name: string
}

interface FunctionSelectProps {
  value: string | null
  options: FunctionOption[]
  onChange: (id: string) => void
  /** Text shown when no option is selected (default "Funktion"). */
  placeholder?: string
  /** Popover width in px (default 150). Widen for long option labels. */
  menuWidth?: number
  sx?: SxProps
}

/**
 * Standard aggregate-function selector (Första, Summa, Min, Max …), reused
 * alongside FieldSelect. A compact white dropdown; the popover marks the
 * current choice with a green check. Keyboard: ↑/↓ move, Enter selects,
 * Esc closes.
 */
const FunctionSelect = (props: FunctionSelectProps) => {
  const {
    value,
    options,
    onChange,
    placeholder = 'Funktion',
    menuWidth = 150,
    sx
  } = props

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const open = Boolean(anchorEl)
  const selected = options.find((option) => option.id === value) ?? null

  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  useEffect(() => {
    if (open) {
      const current = options.findIndex((option) => option.id === value)

      setActiveIndex(current < 0 ? 0 : current)
    }
  }, [open, options, value])

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handlePick = (id: string) => {
    onChange(id)
    handleClose()
  }

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, options.length - 1))
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const option = options[activeIndex]

        if (option) {
          handlePick(option.id)
        }
      } else if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    },
    [options, activeIndex]
  )

  return (
    <>
      <Box
        className="nodrag ignore-locked-mode"
        onClick={handleOpen}
        sx={{
          alignItems: 'center',
          backgroundColor: 'common.white',
          border: `1px solid ${BOARD.border}`,
          borderRadius: '9px',
          cursor: 'pointer',
          display: 'flex',
          gap: 0.5,
          minWidth: '86px',
          p: '7px 9px',
          '&:hover': { backgroundColor: '#F9F9FB', borderColor: '#D4D4D8' },
          ...sx
        }}
      >
        <Box
          sx={{
            color: selected ? BOARD.textPrimary : BOARD.textFaint,
            flex: 1,
            fontFamily: BOARD.fontBody,
            fontSize: '12.5px',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {selected ? selected.name : placeholder}
        </Box>
        <Icon
          name="ExpandMore"
          sx={{ color: BOARD.textFaint, flex: 'none', fontSize: '15px' }}
        />
      </Box>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        disableScrollLock={true}
        onClose={handleClose}
        onKeyDown={handleKeyDown}
        onMouseDown={(event) => event.stopPropagation()}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        transitionDuration={reduceMotion ? 0 : undefined}
        slotProps={{
          paper: {
            sx: {
              border: `1px solid ${BOARD.border}`,
              borderRadius: '11px',
              boxShadow: '0 14px 34px rgba(16, 24, 40, 0.16)',
              mt: '5px',
              overflow: 'hidden',
              p: '5px',
              width: menuWidth
            }
          }
        }}
      >
        <ThemeProvider theme={getTheme('light')}>
          {options.map((option, index) => (
            <Box
              key={option.id}
              onClick={() => handlePick(option.id)}
              onMouseEnter={() => setActiveIndex(index)}
              sx={{
                alignItems: 'center',
                backgroundColor:
                  activeIndex === index ? BOARD.chipBackground : 'transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                gap: 1,
                p: '7px 9px'
              }}
            >
              <Box
                sx={{
                  color: BOARD.textPrimary,
                  flex: 1,
                  fontFamily: BOARD.fontBody,
                  fontSize: '13px',
                  fontWeight: 500
                }}
              >
                {option.name}
              </Box>
              {option.id === value ? (
                <Icon
                  name="Check"
                  sx={{
                    color: BOARD.signalGreen,
                    flex: 'none',
                    fontSize: '15px'
                  }}
                />
              ) : null}
            </Box>
          ))}
        </ThemeProvider>
      </Popover>
    </>
  )
}

export default FunctionSelect
